import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Inventory2,
  Warning,
  CheckCircle,
  Add,
  Edit,
  Delete,
  Search,
  Schedule
} from '@mui/icons-material';
import axios from 'axios';

const InventoryMonitor = ({ add = false }) => {
  const [open, setOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('All');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [inventoryItems, setInventoryItems] = useState([]);

  const [newItem, setNewItem] = useState({
    meal: '',
    quantity: '',
    expiryDate: ''
  });

  const mealChoices = ['Beef', 'Chicken', 'Rice', 'Naan', 'Ice_Cream', 'Salad'];
  const mealCategories = ['All', 'Meat', 'Grains', 'Dairy', 'Vegetables'];

  // Load inventory data from backend
  const loadInventory = async () => {
    setLoading(true);
    try {
      const response = await axios.get('https://stu150255.webhosting.arden.ac.uk/COM6008/api/add-batch/');
      setInventoryItems(response.data.inventory || []);
      setError('');
    } catch (err) {
      console.error('Error loading inventory:', err);
      setError('Failed to load inventory data');
      // Use food items as fallback
      setInventoryItems([
        { id: 1, meal: 'Beef', quantity: 25, expiryDate: '2024-02-15', timestamp: '2024-01-15T10:00:00Z' },
        { id: 2, meal: 'Chicken', quantity: 30, expiryDate: '2024-02-14', timestamp: '2024-01-14T10:00:00Z' },
        { id: 3, meal: 'Rice', quantity: 45, expiryDate: '2024-03-13', timestamp: '2024-01-13T10:00:00Z' },
        { id: 4, meal: 'Naan', quantity: 50, expiryDate: '2024-02-20', timestamp: '2024-01-15T10:00:00Z' },
        { id: 5, meal: 'Ice_Cream', quantity: 15, expiryDate: '2024-02-10', timestamp: '2024-01-12T10:00:00Z' },
        { id: 6, meal: 'Salad', quantity: 20, expiryDate: '2024-01-25', timestamp: '2024-01-14T10:00:00Z' },
      ]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInventory();
  }, []);

  const getStockStatus = (item) => {
    const expiryDate = new Date(item.expiryDate);
    const today = new Date();
    const daysToExpiry = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
    
    if (daysToExpiry <= 3) return { status: 'Expiring Soon', color: 'error' };
    if (daysToExpiry <= 7) return { status: 'Check Soon', color: 'warning' };
    return { status: 'Fresh', color: 'success' };
  };

  const getCategoryFromMeal = (meal) => {
    const mealToCategory = {
      'Beef': 'Meat',
      'Chicken': 'Meat', 
      'Rice': 'Grains',
      'Naan': 'Grains',
      'Ice_Cream': 'Dairy',
      'Salad': 'Vegetables'
    };
    return mealToCategory[meal] || 'Other';
  };

  const handleAddItem = async () => {
    if (newItem.meal && newItem.quantity && newItem.expiryDate) {
      const item = {
        meal: newItem.meal,
        quantity: parseInt(newItem.quantity),
        expiryDate: newItem.expiryDate
      };

      try {
        setLoading(true);
        const response = await axios.post('https://stu150255.webhosting.arden.ac.uk/COM6008/api/add-batch/', item);
        await loadInventory(); // Reload data
        setNewItem({ meal: '', quantity: '', expiryDate: '' });
        setOpen(false);
        setError('');
      } catch (err) {
        console.error('Error adding item:', err);
        setError('Failed to add inventory item');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEditItem = (item) => {
    setEditItem(item);
    setNewItem({
      meal: item.meal,
      quantity: item.quantity.toString(),
      expiryDate: item.expiryDate
    });
    setOpen(true);
  };

  const handleUpdateItem = async () => {
    if (!editItem) return;

    const updatedItem = {
      meal: newItem.meal,
      quantity: parseInt(newItem.quantity),
      expiryDate: newItem.expiryDate
    };

    try {
      setLoading(true);
      // Backend doesn't support PUT method for inventory updates
      // You would need to delete and recreate the item
      await axios.delete(`https://stu150255.webhosting.arden.ac.uk/COM6008/api/add-batch/?id=${editItem.id}`);
      await axios.post('https://stu150255.webhosting.arden.ac.uk/COM6008/api/add-batch/', updatedItem);
      await loadInventory(); // Reload data
      setEditItem(null);
      setNewItem({ meal: '', quantity: '', expiryDate: '' });
      setOpen(false);
      setError('');
    } catch (err) {
      console.error('Error updating item:', err);
      setError('Failed to update inventory item');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteItem = async (id) => {
    try {
      setLoading(true);
      await axios.delete(`https://stu150255.webhosting.arden.ac.uk/COM6008/api/add-batch/?id=${id}`);
      await loadInventory(); // Reload data
      setError('');
    } catch (err) {
      console.error('Error deleting item:', err);
      setError('Failed to delete inventory item');
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = inventoryItems.filter(item => {
    const matchesSearch = item.meal.toLowerCase().includes(searchTerm.toLowerCase());
    const itemCategory = getCategoryFromMeal(item.meal);
    const matchesCategory = filterCategory === 'All' || itemCategory === filterCategory;
    return matchesSearch && matchesCategory;
  });

  const expiringItems = inventoryItems.filter(item => {
    const expiryDate = new Date(item.expiryDate);
    const today = new Date();
    const daysToExpiry = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
    return daysToExpiry <= 7;
  });
  const totalItems = inventoryItems.length;
  const totalQuantity = inventoryItems.reduce((sum, item) => sum + item.quantity, 0);

  const StatCard = ({ title, value, icon, color, subtitle }) => (
    <Card sx={{ height: '100%', background: `linear-gradient(135deg, ${color}15, ${color}05)` }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box sx={{ bgcolor: color, borderRadius: 2, p: 1.5 }}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
          Inventory Monitor
        </Typography>
        {add && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setOpen(true)}
            sx={{
              background: 'linear-gradient(45deg, #667eea, #764ba2)',
              '&:hover': {
                background: 'linear-gradient(45deg, #5a6fd8, #6a4190)',
              }
            }}
          >
            Add Item
          </Button>
        )}
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Expiring Items Alert */}
      {expiringItems.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>{expiringItems.length}</strong> items are expiring soon: {expiringItems.map(item => item.meal).join(', ')}
          </Typography>
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Items"
            value={totalItems}
            icon={<Inventory2 sx={{ color: 'white' }} />}
            color="#667eea"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Expiring Soon"
            value={expiringItems.length}
            icon={<Warning sx={{ color: 'white' }} />}
            color="#f39c12"
            subtitle="Within 7 days"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Fresh Items"
            value={totalItems - expiringItems.length}
            icon={<CheckCircle sx={{ color: 'white' }} />}
            color="#27ae60"
            subtitle="Items in good condition"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Quantity"
            value={totalQuantity}
            icon={<Schedule sx={{ color: 'white' }} />}
            color="#8e44ad"
            subtitle="Total units in stock"
          />
        </Grid>
      </Grid>

      {/* Search and Filter */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search items..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ color: 'action.active', mr: 1 }} />
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Filter by Category</InputLabel>
              <Select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                label="Filter by Category"
              >
                {mealCategories.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Inventory Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Meal Item</strong></TableCell>
                <TableCell><strong>Category</strong></TableCell>
                <TableCell><strong>Quantity</strong></TableCell>
                <TableCell><strong>Expiry Date</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Added</strong></TableCell>
                {add && <TableCell><strong>Actions</strong></TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredItems.map((item) => {
                const stockStatus = getStockStatus(item);
                const itemCategory = getCategoryFromMeal(item.meal);
                return (
                  <TableRow key={item.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {item.meal.replace('_', ' ')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={itemCategory} 
                        size="small" 
                        color="primary" 
                        variant="outlined" 
                      />
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontWeight: 'bold' }}>
                        {item.quantity}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(item.expiryDate).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={stockStatus.status}
                        color={stockStatus.color}
                        variant="outlined"
                        size="small"
                        icon={
                          stockStatus.status === 'Expiring Soon' ? <Warning /> :
                          stockStatus.status === 'Fresh' ? <CheckCircle /> : <Schedule />
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {new Date(item.timestamp).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    {add && (
                      <TableCell>
                        <IconButton onClick={() => handleEditItem(item)} size="small">
                          <Edit />
                        </IconButton>
                        <IconButton onClick={() => handleDeleteItem(item.id)} size="small" color="error">
                          <Delete />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add/Edit Item Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editItem ? 'Edit Item' : 'Add New Item'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Meal Item</InputLabel>
                <Select
                  value={newItem.meal}
                  onChange={(e) => setNewItem({ ...newItem, meal: e.target.value })}
                  label="Meal Item"
                >
                  {mealChoices.map((meal) => (
                    <MenuItem key={meal} value={meal}>
                      {meal.replace('_', ' ')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Quantity"
                type="number"
                value={newItem.quantity}
                onChange={(e) => setNewItem({ ...newItem, quantity: e.target.value })}
                InputProps={{ inputProps: { min: 1 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Expiry Date"
                type="date"
                value={newItem.expiryDate}
                onChange={(e) => setNewItem({ ...newItem, expiryDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={loading}>Cancel</Button>
          <Button 
            onClick={editItem ? handleUpdateItem : handleAddItem} 
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
            sx={{
              background: 'linear-gradient(45deg, #667eea, #764ba2)',
              '&:hover': {
                background: 'linear-gradient(45deg, #5a6fd8, #6a4190)',
              }
            }}
          >
            {loading ? 'Processing...' : (editItem ? 'Update' : 'Add')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InventoryMonitor; 